import { el, clear, fmtInt, fmtDate } from '../ui/dom';
import { renderShell, renderError, renderLoading } from '../ui/shell';
import { listEmails, fetchAllEmails } from '../api/emails';
import type { EmailListItem, EmailListResult, EmailSortField, EmailSortOrder } from '../api/emails';
import { UnauthorizedError } from '../api/client';
import { navigate } from '../router';
import type { RouteContext } from '../router';

const PAGE_SIZE = 50;

export async function renderEmails({ root, query }: RouteContext): Promise<void> {
  const content = renderShell(root, 'emails');

  const state = {
    sortBy: (query.get('sortBy') as EmailSortField) || 'createdAt',
    sortOrder: (query.get('sortOrder') as EmailSortOrder) || 'desc',
    page: Number(query.get('page')) || 1,
  };

  const sortSelect = el('select', { class: 'control' });
  for (const [value, label] of [
    ['createdAt:desc', 'Newest first'],
    ['createdAt:asc', 'Oldest first'],
    ['email:asc', 'Email A–Z'],
    ['email:desc', 'Email Z–A'],
  ] as [string, string][]) {
    const opt = el('option', { value, textContent: label });
    if (value === `${state.sortBy}:${state.sortOrder}`) opt.selected = true;
    sortSelect.append(opt);
  }

  const exportBtn = el('button', { class: 'btn btn-ghost', type: 'button' }, [
    'Export CSV',
  ]);

  const controls = el('div', { class: 'controls' }, [
    el('div', {}, []),
    el('div', { class: 'controls-right' }, [sortSelect, exportBtn]),
  ]);

  const results = el('div', { class: 'results' });
  content.append(
    el('div', { class: 'page-head' }, [el('h1', {}, ['Email List'])]),
    el('p', { class: 'view-caption' }, [
      'Newsletter signups collected via the app, with account creation dates.',
    ]),
    controls,
    results,
  );

  function syncUrl(): void {
    const usp = new URLSearchParams();
    if (state.sortBy !== 'createdAt') usp.set('sortBy', state.sortBy);
    if (state.sortOrder !== 'desc') usp.set('sortOrder', state.sortOrder);
    if (state.page !== 1) usp.set('page', String(state.page));
    const qs = usp.toString();
    history.replaceState({}, '', qs ? `/emails?${qs}` : '/emails');
  }

  async function load(): Promise<void> {
    renderLoading(results, 'Loading emails…');
    syncUrl();
    try {
      const data = await listEmails({
        page: state.page,
        limit: PAGE_SIZE,
        sortBy: state.sortBy,
        sortOrder: state.sortOrder,
      });
      renderTable(results, data);
      renderPagination(results, data, (nextPage) => {
        state.page = nextPage;
        void load();
      });
    } catch (err) {
      if (err instanceof UnauthorizedError) return navigate('/login');
      renderError(results, err instanceof Error ? err.message : 'Failed to load emails.');
    }
  }

  sortSelect.addEventListener('change', () => {
    const [by, order] = sortSelect.value.split(':');
    state.sortBy = by as EmailSortField;
    state.sortOrder = order as EmailSortOrder;
    state.page = 1;
    void load();
  });

  exportBtn.addEventListener('click', async () => {
    exportBtn.setAttribute('disabled', 'true');
    const original = exportBtn.textContent;
    exportBtn.textContent = 'Exporting…';
    try {
      const items = await fetchAllEmails(state.sortBy, state.sortOrder);
      downloadCsv(items);
    } catch (err) {
      renderError(
        results,
        err instanceof Error ? err.message : 'Failed to export emails.',
      );
    } finally {
      exportBtn.removeAttribute('disabled');
      exportBtn.textContent = original;
    }
  });

  await load();
}

function renderTable(container: HTMLElement, data: EmailListResult): void {
  clear(container);

  const summary = el('p', {
    class: 'view-caption',
    style: 'margin:0 0 12px;',
    textContent: `${fmtInt(data.total)} total subscribers`,
  });

  if (data.items.length === 0) {
    container.append(summary, el('div', { class: 'empty' }, ['No email addresses found.']));
    return;
  }

  const rows = data.items.map((item) =>
    el('tr', {}, [
      el('td', {
        class: item.email ? 'cell-strong' : 'cell-muted',
        textContent: item.email || 'No email',
      }),
      el('td', { class: 'cell-muted', textContent: fmtDate(item.createdAt) }),
    ]),
  );

  container.append(
    summary,
    el('div', { class: 'table-wrap' }, [
      el('table', { class: 'data-table' }, [
        el('thead', {}, [
          el('tr', {}, ['Email address', 'Created at'].map((h) => el('th', { textContent: h }))),
        ]),
        el('tbody', {}, rows),
      ]),
    ]),
  );
}

function renderPagination(
  container: HTMLElement,
  data: { page: number; totalPages: number; total: number },
  onPage: (page: number) => void,
): void {
  if (data.total === 0) return;
  const prev = el(
    'button',
    { class: 'btn btn-ghost', disabled: data.page <= 1, onClick: () => onPage(data.page - 1) },
    ['‹ Prev'],
  );
  const next = el(
    'button',
    {
      class: 'btn btn-ghost',
      disabled: data.page >= data.totalPages,
      onClick: () => onPage(data.page + 1),
    },
    ['Next ›'],
  );
  container.append(
    el('div', { class: 'pagination' }, [
      prev,
      el('span', {
        class: 'cell-muted',
        textContent: `Page ${data.page} of ${Math.max(data.totalPages, 1)}`,
      }),
      next,
    ]),
  );
}

function csvEscape(value: string): string {
  if (/[",\n]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

function downloadCsv(items: EmailListItem[]): void {
  const lines = ['Email,Created At'];
  for (const item of items) {
    lines.push(
      [csvEscape(item.email ?? ''), csvEscape(item.createdAt)].join(','),
    );
  }
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `email_list_${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
